import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ThemeProvider as StyledThemeProvider, DefaultTheme } from "styled-components";
import {
  Button,
  Space,
  Col,
  Statistic,
  message,
  Typography,
  ConfigProvider,
} from "antd";
import {
  ReloadOutlined,
  UserOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import { useTheme } from "../../context/ThemeContext";
import { getAntdThemeConfig } from "../../utils/antdTheme";
import UsersTable from "./UsersTable";
import {
  Container,
  StyledCard,
  StatsRow,
  StatCard,
  HeaderSection,
  TopControlBar,
} from "./Styles";
import UserRoleModal from "./UserRoleModal";
import UserDetailsModal from "./UserDetailsModal";
import CustomSelector from "../../commonComponents/Selector";
import CustomInput from "../../commonComponents/input";
import { User } from "./types";
import { AppDispatch, RootState } from "../../redux/store";
import {
  fetchUsers,
  deleteUser,
  updateUserRole,
  fetchRoleStats,
} from "./store/roleManagementSlice";

const { Title } = Typography;

const RoleManagement: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { RoleUsersData, stats, loading, statsLoading } = useSelector(
    (state: RootState) => state.roleManagement
  );
  const { isDarkMode } = useTheme();
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [userRoleModalVisible, setUserRoleModalVisible] = useState<boolean>(false);
  const [detailsModalVisible, setDetailsModalVisible] = useState<boolean>(false);

  const isFetchingRef = useRef<boolean>(false);
  const hasFetchedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!hasFetchedRef.current && !isFetchingRef.current) {
      isFetchingRef.current = true;
      hasFetchedRef.current = true;
      dispatch(fetchUsers()).finally(() => {
        isFetchingRef.current = false;
      });
      // Fetch stats on component mount
      dispatch(fetchRoleStats());
    }
  }, [dispatch]);

  useEffect(() => {
    if (!Array.isArray(RoleUsersData)) {
      setFilteredUsers([]);
      return;
    }

    let filtered = [...RoleUsersData];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (user) => {
          const name = user?.name || user?.username || user?.email || "";
          return name?.toLowerCase().includes(query);
        }
      );
    }

    if (statusFilter === "active") {
      filtered = filtered?.filter((user) => user?.is_active !== false);
    } else if (statusFilter === "inactive") {
      filtered = filtered?.filter((user) => user?.is_active === false);
    }

    setFilteredUsers(filtered);
  }, [RoleUsersData, searchQuery, statusFilter]);

  const handleViewDetails = (user: User) => {
    setSelectedUser(user);
    setDetailsModalVisible(true);
  };

  const handleEditRole = (user: User) => {
    setSelectedUser(user);
    setUserRoleModalVisible(true);
  };

  const handleSaveUserRole = async (roleData: { role: string }) => {
    if (!selectedUser) return;

    setIsSaving(true);
    
    try {
      const result = await dispatch(updateUserRole({
        userId: selectedUser?.id,
        newRole: roleData?.role,
      })).unwrap();
      
      if (result) {
        message.success("User role updated successfully.");
        setFilteredUsers((prev) => 
          prev?.map((u) => 
            u?.id === selectedUser?.id ? { ...u, role: roleData?.role } : u
          )
        );
        setUserRoleModalVisible(false);
        setSelectedUser(null);
      }
    } catch (error: any) {
      message.error(error || "Failed to update user role.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (user: User) => {
    if (!window.confirm(`Are you sure you want to delete ${user.name || user.username || user.email}?`)) {
      return;
    }

    try {
      const result = await dispatch(deleteUser(user?.id)).unwrap();
      if (result) {
        message.success("User deleted successfully.");
        setFilteredUsers((prev) => prev.filter((u) => u.id !== user.id));
      }
    } catch (error: any) {
      message.error(error || "Failed to delete user.");
    }
  };

  const handleRefresh = () => {
    if (!isFetchingRef.current && !loading) {
      isFetchingRef.current = true;
      dispatch(fetchUsers()).finally(() => {
        isFetchingRef.current = false;
      });
      // Refresh stats as well
      dispatch(fetchRoleStats());
    }
  };

  const paginatedUsers = (Array.isArray(filteredUsers) ? filteredUsers : []).slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const themeMode: 'light' | 'dark' = isDarkMode ? "dark" : "light";
  const styledTheme = { mode: themeMode } as DefaultTheme;

  return (
    <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
      <StyledThemeProvider theme={styledTheme}>
    <Container>
      <HeaderSection>
            <Title level={2} style={{ margin: 0, color: isDarkMode ? "#fff" : undefined }}>
          Role Management
        </Title>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>
      </HeaderSection>
      <StatsRow gutter={[12, 16]}>
        <Col xs={24} sm={12} md={6} flex="1" style={{ minWidth: 0, display: 'flex' }}>
          <StatCard cardType="total">
            <Statistic
              title="Total Users"
                value={stats?.totalUsers ?? 0}
              valueStyle={{ color: "#1890ff" }}
              prefix={<UserOutlined style={{ color: "#1890ff" }} />}
                loading={statsLoading}
            />
          </StatCard>
        </Col>
        <Col xs={24} sm={12} md={6} flex="1" style={{ minWidth: 0, display: 'flex' }}>
          <StatCard cardType="active">
            <Statistic
              title="Active Users"
                value={stats?.activeUsers ?? 0}
              valueStyle={{ color: "#52c41a" }}
              prefix={<UserOutlined style={{ color: "#52c41a" }} />}
                loading={statsLoading}
            />
          </StatCard>
        </Col>
        <Col xs={24} sm={12} md={6} flex="1" style={{ minWidth: 0, display: 'flex' }}>
          <StatCard cardType="withRoles">
            <Statistic
              title="Users with Roles"
                value={stats?.usersWithRoles ?? 0}
              valueStyle={{ color: "#1890ff" }}
              prefix={<SafetyOutlined style={{ color: "#1890ff" }} />}
                loading={statsLoading}
            />
          </StatCard>
        </Col>
        <Col xs={24} sm={12} md={6} flex="1" style={{ minWidth: 0, display: 'flex' }}>
          <StatCard cardType="withoutRoles">
            <Statistic
              title="Users without Roles"
                value={stats?.usersWithoutRoles ?? 0}
              valueStyle={{ color: "#fa8c16" }}
              prefix={<UserOutlined style={{ color: "#fa8c16" }} />}
                loading={statsLoading}
            />
          </StatCard>
        </Col>
      </StatsRow>
      <StyledCard>
        <TopControlBar>
            <CustomInput
              placeholder="Search users by name..."
              value={searchQuery}
              onChange={(value: string) => setSearchQuery(value)}
              allowClear
              width="300px"
            />
            <CustomSelector
              value={statusFilter}
              onChange={(value: string) => setStatusFilter(value)}
              options={[
                { value: "all", label: "All Status" },
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
              ]}
              width={109}
              height={36}
              borderRadius={8}
            />
        </TopControlBar>
        <UsersTable
          users={paginatedUsers}
          currentPage={currentPage}
          pageSize={pageSize}
          total={Array.isArray(filteredUsers) ? filteredUsers.length : 0}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          onViewDetails={handleViewDetails}
          onEditRole={handleEditRole}
          onDelete={handleDelete}
          loading={loading}
        />
      </StyledCard>
      <UserRoleModal
        visible={userRoleModalVisible}
        onClose={() => {
          setUserRoleModalVisible(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onSave={handleSaveUserRole}
        isLoading={false}
        isSaving={isSaving}
      />
      <UserDetailsModal
        visible={detailsModalVisible}
        onClose={() => {
          setDetailsModalVisible(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onEditRole={handleEditRole}
      />
    </Container>
      </StyledThemeProvider>
    </ConfigProvider>
  );
};

export default RoleManagement;

